<template>
  <div class="ride__container">
    <transition name="fade">
      <div
        v-if="activeGroupDate"
        v-html="activeGroupDate"
        class="ride__date ride__date--fixed"
      />
    </transition>

    <div
      class="overflow--y"
      :style="`height: ${contentHeight}px`"
      @scroll="loadMore"
    >
      <transition name="fade">
        <div v-if="isSuccess && isValidArray(ridesGroupByDate)">
          <div
            v-for="ridesGroup in ridesGroupByDate"
            :key="ridesGroup.date"
            :data-date="ridesGroup.date"
            class="group"
          >
            <div v-html="dateHtmlFormated(ridesGroup.date)" class="ride__date" />
            <div v-for="ride in ridesGroup.rideList" :key="ride.id">
              <RideCard
                :data="ride"
                link
                @click="change({
                  type: 'ride',
                  action: null
                }, {
                  id: ride.taxiJobId,
                  name: null,
                  startDate: ride.start.date
                })"
              />
            </div>
          </div>
        </div>
      </transition>

      <transition name="fade">
        <v-layout
          v-if="isSuccess && !isValidArray(ridesGroupByDate)"
          align-center
          justify-center
          column
          fill-height
        >
          <EmptyState
            v-if="drawerType !== 'home'"
            :type="hasReceiptsRole ? 'ride' : 'employee'"
            :imageSize="120"
            class="px-4"
          >
            <span
              v-html="hasReceiptsRole ? $t('Views.ReportsPage.emptyStateText') : $t('Common.withoutPermission')"
              class="state__text"
            />
          </EmptyState>

          <v-layout v-else class="text--center text--small" align-center justify-center column fill-height>
            <p><strong v-html="$t('Views.ReportsPage.emptyStateText')" /></p>
            <p>
              <span v-html="$t('Views.ReportsPage.emptyStateDisclaimer')" /><br class="hidden-sm-and-down"><a href="https://99empresas.zendesk.com/hc/pt-br" :title="$t('Common.clickHere')" target="_blank" class="text--link accent--text">{{ $t('Common.clickHere') }}</a>&nbsp;<span v-html="$t('Views.ReportsPage.knowHow')" />
            </p>
          </v-layout>
        </v-layout>
      </transition>

      <transition name="fade">
        <v-layout v-if="isFailed" class="text--center text--small" align-center justify-center column fill-height>
          {{ $t('Common.boxGenericError') }}<br>
          <a
            :title="$t('Common.tryAgain')"
            class="accent--text"
            @click.prevent="tryAgain"
          >
            {{ $t('Common.tryAgain') }}
          </a>
        </v-layout>
      </transition>

      <transition name="fade">
        <v-layout
          v-if="isLoading || isFetching"
          class="py-5"
          align-center
          justify-center
        >
          <v-progress-circular color="primary" indeterminate />
        </v-layout>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'libs/moment'
import { hasRole } from 'utils/functions'
import { isValidArray, isValidObject, calcContentHeight, removeDuplicateItemsInArray, catchError } from 'corp-front-vue-commons/src/utils/functions'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import RideCard from 'components/GlobalDrawer/RideList/RideCard'
import EmptyState from 'components/EmptyState'

export default {
  name: 'RideList',
  props: {
    data: {
      type: Object,
      required: true
    },
    drawerType: {
      type: String,
      required: true
    },
    filters: {
      type: Object
    },
    scrollCalcDiff: {
      type: Number,
      default: 0
    }
  },
  components: {
    RideCard,
    EmptyState
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany,
      claims: ({ userClaims }) => userClaims.claims,
      selectedlocale: ({ locale }) => locale.symbols.lang,
      selectedlocaleDate: ({ locale }) => locale.symbols.date
    }),

    isLoading () {
      return this.currentStatus === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },

    isFailed () {
      return this.currentStatus === STATUS_FAILED
    },

    hasReceiptsRole () {
      if (this.drawerType === 'financial') {
        // CLOSED INVOICE
        if (this.data && this.data.orderId) {
          return this.hasRole('billing', 'ROLE_GET_ORDER_SERVICE_RECEIPTS')
        }

        // OPEN INVOICE
        return this.hasRole('billing', 'ROLE_GET_ORDER_PREVIEW_RIDE_SERVICE_RECEIPTS')
      }

      return this.hasRole('report', 'ROLE_GET_REPORT_TAXIJOBS')
    }
  },
  data () {
    return {
      currentStatus: STATUS_IS_LOADING,
      isFetching: false,
      contentHeight: 'none',
      page: 1,
      limit: 25,
      rides: [],
      ridesGroupByDate: [],
      lastPage: 0,
      activeGroupDate: null
    }
  },
  methods: {
    isValidArray (val) {
      return isValidArray(val)
    },

    isValidObject (val) {
      return isValidObject(val)
    },

    hasRole (group, role) {
      return hasRole(group, role)
    },

    resetAttributes () {
      this.currentStatus = STATUS_IS_LOADING
      this.isFetching = false
      this.contentHeight = 'none'
      this.page = 1
      this.limit = 25
      this.rides = []
      this.ridesGroupByDate = []
      this.lastPage = 0
      this.activeGroupDate = null
    },

    change (drawer, data) {
      this.$emit('change', {
        drawer: {
          type: drawer.type,
          action: drawer.action
        },
        data: {
          id: data.id,
          name: data.name,
          startDate: data.startDate
        }
      })
    },

    calcContentHeight () {
      const $tabs = document.querySelector('.drawer .v-tabs__bar')
      const $tabsHeight = $tabs ? $tabs.offsetHeight : 0
      const $status = document.querySelector('.drawer .invoice__status')
      const $statusHeight = $status ? $status.offsetHeight : 0
      const calc = $tabsHeight + $statusHeight
      this.contentHeight = calcContentHeight() - calc - this.scrollCalcDiff
    },

    setItemDate (wrapper) {
      Array.from(document.querySelectorAll('.group')).forEach($item => {
        if (wrapper.scrollTop <= 0) {
          this.activeGroupDate = null
          return
        }

        if (wrapper.scrollTop >= $item.offsetTop) {
          this.activeGroupDate = this.dateHtmlFormated($item.dataset.date)
        }
      })
    },

    loadMore (event) {
      this.setItemDate(event.target)

      if (this.isFetching || this.page >= this.lastPage) return

      const wrapper = event.target
      const cardHeight = 127 * 4

      if (wrapper.offsetHeight + wrapper.scrollTop >= (wrapper.scrollHeight - cardHeight)) {
        this.isFetching = true
        this.page += 1

        setTimeout(async () => {
          await this.loadData()
          this.isFetching = false
        }, 100)
      }
    },

    dateHtmlFormated (str) {
      return str.replace(/(\d{2}\/\d{2}\/\d{4})(.+)/, '<strong>$1</strong>,$2')
    },

    formatDate (date) {
      return `${moment(date).format(this.selectedlocaleDate.full)} ${moment(date).format('dddd')}`
    },

    dayGroup (data) {
      const rides = this.drawerType === 'financial'
        ? data.map(item => this.formatDate(item.receipt.corporateJob.start.date))
        : data.map(item => this.formatDate(item.start.date))

      return removeDuplicateItemsInArray(rides)
    },

    tryAgain () {
      this.currentStatus = STATUS_IS_LOADING
      this.rides = []
      this.loadData()
    },

    async getReceipts () {
      try {
        const pageDecrement = this.page - 1
        let payload = {}

        if (this.data && this.data.orderId) {
          // closed invoice
          if (this.hasRole('billing', 'ROLE_GET_ORDER_SERVICE_RECEIPTS')) {
            const { data: res } = await this.$apiService.getRefundReceipts(
              this.data.orderId,
              { size: this.limit },
              { page: pageDecrement },
              { orderBy: 'desc' }
            )

            payload = res ? { ...res } : {}
          }
        } else {
          // open invoice
          if (this.hasRole('billing', 'ROLE_GET_ORDER_PREVIEW_RIDE_SERVICE_RECEIPTS')) {
            const companyId = this.selectedCompany ? this.selectedCompany.id : null
            const { data: res } = await this.$apiService.getOpenInvoiceReceipts(
              { companyId },
              { size: this.limit },
              { page: pageDecrement },
              { orderBy: 'desc' }
            )

            payload = res ? { ...res } : {}
          }
        }

        if (this.isValidObject(payload) && !payload.valid) {
          this.currentStatus = STATUS_FAILED

          const msg = this.isValidArray(payload.reasons)
            ? payload.reasons.join(', ')
            : payload.message

          catchError(msg)
          throw new Error()
        }

        if (!this.isValidObject(payload) || (this.isValidObject(payload) && !payload.content)) {
          this.rides = []
          this.currentStatus = STATUS_SUCCESS
          return
        }

        this.lastPage = payload.totalPages
        this.rides = this.rides.concat(payload.content)

        this.ridesGroupByDate = this.dayGroup(this.rides).map(date => {
          const rideList = this.drawerType === 'financial'
            ? this.rides
              .filter(item => this.formatDate(item.receipt.corporateJob.start.date) === date)
              .map(item => {
                const { employee, start, end, odometer, fare } = item.receipt.corporateJob

                return {
                  employee: { name: employee.name },
                  start: { date: start.date, address: start.address },
                  end: { date: end.date, address: end.address },
                  odometer,
                  fare,
                  chargeBack: item.chargeBack || null,
                  taxiJobId: item.receipt.corporateJob.taxiJobId
                }
              })
            : this.rides.filter(item => this.formatDate(item.start.date) === date)

          return {
            date,
            rideList
          }
        })

        this.calcContentHeight()
        this.currentStatus = STATUS_SUCCESS
      } catch (error) {
        catchError(error)
        this.currentStatus = STATUS_FAILED
      }
    },

    async getRideList () {
      try {
        if (!this.hasRole('report', 'ROLE_GET_REPORT_TAXIJOBS')) {
          this.currentStatus = STATUS_SUCCESS
          return
        }

        const { page, limit } = this
        const companyId = this.selectedCompany ? this.selectedCompany.id : null
        const startDate = this.filters && this.filters.startDate ? this.filters.startDate : null
        const endDate = this.filters && this.filters.endDate ? this.filters.endDate : null
        let employeeId = this.filters && this.filters.employeeId ? this.filters.employeeId : null
        let costCenterId = this.filters && this.filters.costCenterId ? this.filters.costCenterId : null

        if (this.drawerType === 'employee') {
          employeeId = this.data.id || this.data.employeeId
          costCenterId = null
        }

        if (this.drawerType === 'costCenter') {
          employeeId = null
          costCenterId = this.data.id || this.data.costCenterId
        }

        const { data: res } = await this.$apiService.reports.getRides(
          { page },
          { limit },
          { companyId },
          { startDate },
          { endDate },
          { employeeId },
          { costCenterId },
          { orderBy: 'desc' }
        )

        this.lastPage = Math.ceil(res.length / limit)
        this.rides = this.rides.concat(res.data)
        this.ridesGroupByDate = this.dayGroup(this.rides).map(date => {
          return {
            date,
            rideList: this.rides.filter(item => this.formatDate(item.start.date) === date)
          }
        })

        this.calcContentHeight()
        this.currentStatus = STATUS_SUCCESS
      } catch (error) {
        catchError(error)
        this.currentStatus = STATUS_FAILED
      }
    },

    async loadData () {
      if (this.drawerType === 'financial') {
        await this.getReceipts()
        return
      }

      await this.getRideList()

      this.currentStatus = STATUS_SUCCESS
    }
  },
  watch: {
    data: {
      deep: true,
      handler (val) {
        this.resetAttributes()
        this.loadData()
      }
    },
    selectedCompany () {
      if (this.drawerType === 'home') {
        this.resetAttributes()
        this.loadData()
      }
    }
  },
  mounted () {
    this.loadData()
    this.calcContentHeight()
    window.addEventListener('resize', this.calcContentHeight)
  },
  beforeDestroy () {
    window.addEventListener('resize', this.calcContentHeight)
  }
}
</script>

<style lang="scss" scoped>
.ride__date {
  background-color: $gray-2;
  font-family: $body-font;
  font-size: $base-font-size;
  line-height: normal;
  padding: 10px 0;
  text-align: center;
}

.ride__container {
  position: relative;
}

.ride__date--fixed {
  background-color: $white;
  border-bottom: 1px solid $gray-4;
  left: 0;
  padding: 9.5px 0;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
}
</style>
